/**
 * The **attack glance** (`comms-attack-reaction-cards` 2.1) — the structured, per-target summary
 * the collapsed COMMS attack card renders: one headline row per caught target (outcome stamp, the
 * damage it took by type, `21 beats EVA 8`), a line naming what fired, and a few chips for what
 * changed. The expanded breakdown under it stays the narrator's receipt ({@link
 * import("./narrate").narrateResolution}); this module never produces prose.
 *
 * **Same governing rule as the narrator: the record is the only input** (design doc requirement 5).
 * Steps plus the `about` breadcrumb plus a caller-supplied name resolver — no compendium, no live
 * encounter state. A fact the record does not carry is absent from the glance, never guessed.
 *
 * **Per target, never summed across targets** (requirement 9, `projects/completed/aoe-attack-
 * headline.md`). An area attack yields one {@link GlanceTarget} per caught target, split by the
 * receipt's own seam ({@link segmentAttack}), and nothing here adds two targets' numbers together.
 * A card that wants a group header total (`5 TO ASSAULT`) sums per target itself.
 *
 * **Chips name events, never remainders** (`comms-attack-reaction-cards` settled item 3,
 * `decisions/0082`). `−1 STRUCTURE` is a witnessed event, like per-hit damage; "2 structure left"
 * is cumulative state, the thing 0082 hides for a hostile's HP. No chip reads a `table_check`'s
 * remaining track or any other absolute track value.
 *
 * **Redaction is upstream and already applied.** A class-tier hostile's record arrives with
 * `about.weapon` dropped and `about.techOption.name` reading the sentinel (`EncounterRoom.
 * broadcastResolution`); the glance then has no weapon line and shows the sentinel option. It reads
 * no source name off any step, so the step walk's sentinels never reach it either way.
 */
import type { CombatantId, DamageStep, MissChanceStep, ResolutionAbout, ResolvedStep, ToHitStep } from "@lancer-vtt/domain";
import { UNDISCLOSED_WEAPON_NAME } from "@lancer-vtt/domain";
import { chainParts, isTechAttack, linkCauseName, segmentAttack, type ChainPart, type NameOf } from "./narrate";
import { rollVerdict, type RollVerdict } from "./verdict";

/** The outcome stamp. A pre-roll miss chance (the Invisible coin) is a `MISS` with no {@link GlanceTarget.toHit}. */
export type GlanceOutcome = "HIT" | "CRIT" | "MISS";

/** What a chip reports — one kind per event family, so a card can style or order them. */
export type GlanceChipKind = "status_gained" | "status_lost" | "heat" | "structure" | "stress" | "destroyed";

/** One chip: `+IMPAIRED`, `−LOCK ON`, `+2 HEAT`, `−1 STRUCTURE`, `−1 STRESS`, `DESTROYED`. Never a remainder. */
export type GlanceChip = { kind: GlanceChipKind; text: string };

/** A combatant as the card names it: the id the record keys by, and its display name. */
export type GlanceParty = { id: CombatantId; name: string };

/** One caught target's headline row. */
export type GlanceTarget = {
  target: GlanceParty;
  outcome: GlanceOutcome;
  /**
   * The damage this target took, one entry per damage type in first-recorded order — each the sum
   * of that type's post-mitigation `DamageStep.final` (an immune step dealt nothing and is
   * skipped). Heat-typed damage is an entry like any other (`2 HEAT`). Empty for a miss and for a
   * tech attack, which roll no damage. A hit armor fully absorbed reads `{ total: 0 }` — the
   * record says the shot landed and did nothing, and the card should say so.
   */
  damage: { total: number; dtype: DamageStep["dtype"] }[];
  /**
   * The to-hit line (`21 beats EVA 8`), with the verb read off the recorded result ({@link rollVerdict}).
   * Absent when the shot missed on the pre-roll coin — see {@link missChance}.
   */
  toHit?: { total: number; verdict: RollVerdict; vs: "EVA" | "E-DEF"; def: number };
  /** The recorded miss probability when the shot missed outright on the Invisible coin, before any d20. */
  missChance?: number;
  chips: GlanceChip[];
};

/** The whole glance for one resolution. */
export type AttackGlance = {
  actor: GlanceParty;
  /**
   * What fired, off `about.weapon`: its name, its area template as the card prints it
   * (`"Blast 2"`), and whether this attack was melee. Absent for a tech attack, for a class-tier
   * hostile (its weapon is redacted off the record), and for an old entry that never recorded one.
   */
  weapon?: { name: string; area?: string; melee?: true };
  /**
   * Present iff this was a TECH attack (its to-hit rolled against E-Defense): the action's RAW name
   * (`"Invade"`) and the option it fired (`"Fragment Signal"`, or the `undisclosed feature`
   * sentinel for a class-tier hostile), each only when `about.techOption` recorded it.
   */
  tech?: { action?: string; option?: string };
  /** The multi-shot group key (`about.shot`), passed through for the card's grouping. */
  shot?: NonNullable<ResolutionAbout["shot"]>;
  /** This shot was an Auxiliary rider and dealt no bonus damage (`about.noBonus`). */
  noBonus?: true;
  targets: GlanceTarget[];
  /**
   * The chain links that followed the primary attack (`decisions/0210`), one glance per link, in
   * recorded order: Aggression's forced Chain Axe swing after Bret's Shotgun hit. The primary's own
   * fields read only the steps before the first link, so a unit the chain destroyed is not
   * credited to the primary attacker. Absent when the resolution has no `link` step.
   */
  links?: GlanceLink[];
  /**
   * The reactions that interrupted this attack (`chained-resolution-sequence` 4.3): a Brace, a
   * Rebound, a target's on-damage clause, each with its reactor and chips. Absent when none did.
   */
  reactions?: GlanceReaction[];
  /**
   * What the attack did to its OWN actor — a `tg_heat_self` or Overkill heat tick, an overheat's
   * stress loss, a status the attack cost it (a Hidden attacker revealing itself). Empty when the
   * actor is also one of {@link targets} (its chips are on that row instead).
   */
  self: GlanceChip[];
};

/** `CRIT` / `HIT` / `MISS` off a recorded to-hit result. */
/** One chain link's glance: what caused it (`Aggression`), the random pick when one chose its target, and the attack itself. */
export type GlanceLink = { cause: string; pick?: string; glance: AttackGlance };

/** A reaction that interrupted an attack (4.3): `BRACE` by pc1, `+BRACED`. */
export type GlanceReaction = { cause: string; actor: GlanceParty; chips: GlanceChip[] };

function outcomeOf(result: ToHitStep["result"]): GlanceOutcome {
  return result === "crit" ? "CRIT" : result === "hit" ? "HIT" : "MISS";
}

/** Tight signed number: `+2` / `−1` (unicode minus), the narrator's own style for a delta. */
function signed(n: number): string {
  return n >= 0 ? `+${n}` : `−${Math.abs(n)}`;
}

/**
 * The chips for one combatant, read off the WHOLE step list filtered to that combatant — the same
 * recorded-order, subject-scoped walk the receipt's trailing block does. `skipHeat` suppresses the
 * heat chip when the target already shows heat-typed DAMAGE: heat damage routes through the heat
 * cascade and records a `heat_delta` for the same heat (the narrator's "one fact seen twice"), and
 * the glance says it once.
 */
function chipsFor(steps: readonly ResolvedStep[], who: CombatantId, skipHeat: boolean): GlanceChip[] {
  const chips: GlanceChip[] = [];
  let heat = 0;
  let structure = 0;
  let stress = 0;
  let destroyed = false;
  for (const s of steps) {
    if (!("target" in s) || s.target !== who) continue;
    if (s.op === "status_add") chips.push({ kind: "status_gained", text: `+${s.status.toUpperCase()}` });
    else if (s.op === "status_remove") chips.push({ kind: "status_lost", text: `−${s.status.toUpperCase()}` });
    else if (s.op === "heat_delta") heat += s.delta;
    else if (s.op === "structure_delta") structure += s.delta;
    else if (s.op === "stress_delta") stress += s.delta;
    else if (s.op === "destroy") destroyed = s.destroyed;
  }
  if (heat !== 0 && !skipHeat) chips.push({ kind: "heat", text: `${signed(heat)} HEAT` });
  // Only a net LOSS is an event worth a chip: a successful interception records a compensating
  // `+1` beside the `−1` it un-took, which nets to nothing happening.
  if (structure < 0) chips.push({ kind: "structure", text: `${signed(structure)} STRUCTURE` });
  if (stress < 0) chips.push({ kind: "stress", text: `${signed(stress)} STRESS` });
  if (destroyed) chips.push({ kind: "destroyed", text: "DESTROYED" });
  return chips;
}

/** Per-type damage totals for one target, in first-recorded order. */
function damageFor(steps: readonly ResolvedStep[], who: CombatantId): GlanceTarget["damage"] {
  const totals = new Map<DamageStep["dtype"], number>();
  for (const s of steps) {
    if (s.op !== "damage" || s.immune || (s.target ?? who) !== who) continue;
    totals.set(s.dtype, (totals.get(s.dtype) ?? 0) + s.final);
  }
  return [...totals].map(([dtype, total]) => ({ total, dtype }));
}

/** `Blast 2` / `Burst` — the area template as the card prints it (a size of 0 prints the bare word). */
function areaLabel(area: NonNullable<NonNullable<ResolutionAbout["weapon"]>["area"]>): string {
  const { kind: shape, size } = area;
  const word = shape[0]!.toUpperCase() + shape.slice(1);
  return size > 0 ? `${word} ${size}` : word;
}

/**
 * Summarize one attack resolution for the collapsed card, or `undefined` when the step list holds
 * no attack at all (no `to_hit`, no `miss_chance`) — a save-only or bookkeeping resolution, which
 * the card renders some other way.
 *
 * The glance reads a fixed handful of ops and ignores the rest; the receipt behind it itemizes
 * every op (`narrateResolution`).
 */
export function summarizeAttackGlance(args: { steps: readonly ResolvedStep[]; about?: ResolutionAbout; names?: NameOf }): AttackGlance | undefined {
  if (!args.steps.some((s) => s.op === "link")) return glanceOf(args);
  const names: NameOf = args.names ?? ((id) => id);
  const [first, ...rest] = chainParts(args.steps);
  const primary = glanceOf({ ...args, steps: first!.steps });
  if (primary === undefined) return undefined;
  const links: GlanceLink[] = [];
  for (const part of rest) {
    const link = part.link!;
    const weapon = link.weapon !== undefined && link.weapon.name !== UNDISCLOSED_WEAPON_NAME ? { weapon: link.weapon } : {};
    const glance = glanceOf({ steps: part.steps, about: { actor: link.actor, ...weapon }, names });
    if (glance === undefined) continue;
    const pick = link.pick?.roll !== undefined ? `d${link.pick.from.length} = ${link.pick.roll}` : undefined;
    links.push({ cause: linkCauseName(link), ...(pick !== undefined ? { pick } : {}), glance: withReactions(glance, part, names) });
  }
  const head = withReactions(primary, first!, names);
  return links.length > 0 ? { ...head, links } : head;
}

/** A part's glance with the reactions that interrupted it (4.3): who reacted, with what, and its chips. */
function withReactions(glance: AttackGlance, part: ChainPart, names: NameOf): AttackGlance {
  if (part.reactions.length === 0) return glance;
  const reactions: GlanceReaction[] = part.reactions.map(({ link, steps }) => ({ cause: linkCauseName(link), actor: { id: link.actor, name: names(link.actor) }, chips: chipsFor(steps, link.actor, false) }));
  return { ...glance, reactions };
}

/** One link's glance — {@link summarizeAttackGlance} before chains existed. */
function glanceOf(args: { steps: readonly ResolvedStep[]; about?: ResolutionAbout; names?: NameOf }): AttackGlance | undefined {
  const { steps, about } = args;
  const names: NameOf = args.names ?? ((id) => id);
  const { segments } = segmentAttack(steps);
  if (segments.length === 0) return undefined;
  // A single-target attack reads the WHOLE list (steps before its head included), exactly as the
  // narrator does; an area fan-out reads each caught target's own segment.
  const blocks = segments.length === 1 ? [{ head: segments[0]![0]!, steps }] : segments.map((seg) => ({ head: seg[0]!, steps: seg as readonly ResolvedStep[] }));

  const targets: GlanceTarget[] = [];
  let tech = false;
  for (const { head, steps: block } of blocks) {
    if (head.op !== "to_hit" && head.op !== "miss_chance") continue;
    const id = head.target;
    const damage = damageFor(block, id);
    const chips = chipsFor(steps, id, damage.some((d) => d.dtype === "heat"));
    if (head.op === "miss_chance") {
      targets.push({ target: { id, name: names(id) }, outcome: "MISS", damage: [], missChance: head.chance, chips });
      continue;
    }
    if (isTechAttack(head, about)) tech = true;
    targets.push({
      target: { id, name: names(id) },
      outcome: outcomeOf(head.result),
      damage,
      toHit: { total: head.total, verdict: rollVerdict(head.total, head.defense, head.result), vs: head.vs === "edef" ? "E-DEF" : "EVA", def: head.defense },
      chips,
    });
  }

  // Every segment opens at a `to_hit` or `miss_chance` (`segmentAttack`), and both record the attacker.
  const first = segments[0]![0] as ToHitStep | MissChanceStep;
  const actorId = about?.actor ?? first.attacker;
  const actorIsTarget = targets.some((t) => t.target.id === actorId);
  const w = about?.weapon;
  const option = about?.techOption;
  return {
    actor: { id: actorId, name: names(actorId) },
    ...(w !== undefined && !tech
      ? { weapon: { name: w.name, ...(w.area !== undefined ? { area: areaLabel(w.area) } : {}), ...(w.melee ? { melee: true as const } : {}) } }
      : {}),
    ...(tech ? { tech: { ...(option?.action !== undefined ? { action: option.action } : {}), ...(option !== undefined ? { option: option.name } : {}) } } : {}),
    ...(about?.shot !== undefined ? { shot: about.shot } : {}),
    ...(about?.noBonus ? { noBonus: true as const } : {}),
    targets,
    self: actorIsTarget ? [] : chipsFor(steps, actorId, false),
  };
}
